/**
 * API Contract Designer Types
 * Design and document APIs with OpenAPI spec generation
 */

// HTTP Methods
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD' | 'OPTIONS';

// Parameter location
export type ParameterLocation = 'path' | 'query' | 'header' | 'cookie';

// Schema types
export type SchemaType = 'string' | 'number' | 'integer' | 'boolean' | 'array' | 'object';

// String formats
export type StringFormat = 'date' | 'date-time' | 'email' | 'uri' | 'uuid' | 'hostname' | 'ipv4' | 'ipv6' | 'password' | 'byte' | 'binary';

// Security scheme types
export type SecuritySchemeType = 'apiKey' | 'http' | 'oauth2' | 'openIdConnect';

// API key location
export type ApiKeyLocation = 'header' | 'query' | 'cookie';

// Schema definition
export interface SchemaDefinition {
  type: SchemaType;
  format?: StringFormat;
  description?: string;
  required?: boolean | string[];  // boolean for property, string[] for object's required fields
  nullable?: boolean;
  default?: unknown;
  enum?: string[];
  minimum?: number;
  maximum?: number;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  items?: SchemaDefinition;  // For arrays
  properties?: Record<string, SchemaDefinition>;  // For objects
  additionalProperties?: boolean | SchemaDefinition;
  $ref?: string;  // Reference to schema component
  example?: unknown;
}

// Parameter definition
export interface Parameter {
  id: string;
  name: string;
  in: ParameterLocation;
  description?: string;
  required: boolean;
  deprecated?: boolean;
  schema: SchemaDefinition;
  example?: unknown;
}

// Request body
export interface RequestBody {
  description?: string;
  required: boolean;
  content: Record<string, MediaType>;  // e.g., 'application/json'
}

// Media type
export interface MediaType {
  schema: SchemaDefinition;
  example?: unknown;
  examples?: Record<string, Example>;
}

// Example object
export interface Example {
  summary?: string;
  description?: string;
  value: unknown;
}

// Response definition
export interface Response {
  id: string;
  statusCode: string;  // '200', '201', '400', etc.
  description: string;
  headers?: Record<string, Header>;
  content?: Record<string, MediaType>;
}

// Header definition
export interface Header {
  description?: string;
  required?: boolean;
  schema: SchemaDefinition;
}

// Endpoint definition
export interface Endpoint {
  id: string;
  path: string;
  method: HttpMethod;
  operationId?: string;
  summary?: string;
  description?: string;
  tags?: string[];
  parameters: Parameter[];
  requestBody?: RequestBody;
  responses: Response[];
  deprecated?: boolean;
  security?: SecurityRequirement[];
}

// Security requirement
export interface SecurityRequirement {
  schemeName: string;
  scopes?: string[];  // For OAuth2
}

// Schema component (reusable)
export interface SchemaComponent {
  id: string;
  name: string;
  schema: SchemaDefinition;
  description?: string;
}

// Security scheme
export interface SecurityScheme {
  id: string;
  name: string;
  type: SecuritySchemeType;
  description?: string;
  // API key
  apiKeyName?: string;
  apiKeyIn?: ApiKeyLocation;
  // HTTP
  httpScheme?: 'bearer' | 'basic' | 'digest';
  bearerFormat?: string;
  // OAuth2
  oauth2Flows?: OAuth2Flows;
  // OpenID Connect
  openIdConnectUrl?: string;
}

// OAuth2 flows
export interface OAuth2Flows {
  implicit?: OAuth2Flow;
  password?: OAuth2Flow;
  clientCredentials?: OAuth2Flow;
  authorizationCode?: OAuth2Flow;
}

// OAuth2 flow
export interface OAuth2Flow {
  authorizationUrl?: string;
  tokenUrl?: string;
  refreshUrl?: string;
  scopes: Record<string, string>;
}

// Server definition
export interface Server {
  id: string;
  url: string;
  description?: string;
  variables?: Record<string, ServerVariable>;
}

// Server variable
export interface ServerVariable {
  default: string;
  description?: string;
  enum?: string[];
}

// API version
export interface APIVersion {
  id: string;
  version: string;
  createdAt: string;
  snapshot: string;  // JSON stringified contract state
  notes?: string;
}

// Consumer definition
export interface Consumer {
  id: string;
  name: string;
  description?: string;
  contactEmail?: string;
  endpointsUsed: string[];  // Endpoint IDs
  createdAt: string;
}

// Breaking change
export interface BreakingChange {
  type: 'removed_endpoint' | 'removed_parameter' | 'required_parameter_added' | 'response_schema_changed' | 'type_changed' | 'security_changed';
  severity: 'high' | 'medium' | 'low';
  path?: string;
  method?: HttpMethod;
  field?: string;
  description: string;
  affectedConsumers?: string[];
}

// Full API Contract
export interface APIContract {
  id: string;
  name: string;
  version: string;
  description?: string;
  termsOfService?: string;
  contact?: {
    name?: string;
    email?: string;
    url?: string;
  };
  license?: {
    name: string;
    url?: string;
  };
  servers: Server[];
  endpoints: Endpoint[];
  schemas: SchemaComponent[];
  securitySchemes: SecurityScheme[];
  tags?: Tag[];
  versions: APIVersion[];
  consumers: Consumer[];
  createdAt: string;
  updatedAt: string;
}

// Tag definition
export interface Tag {
  name: string;
  description?: string;
}

// Feature access for premium gating
export interface APIContractFeatures {
  canEdit: boolean;
  canSave: boolean;
  maxContracts: number;
  maxEndpoints: number;
  canImport: boolean;
  canExportYaml: boolean;
  canExportMarkdown: boolean;
  canDetectBreakingChanges: boolean;
  canGenerateMocks: boolean;
  canTrackConsumers: boolean;
}

// HTTP method configuration
export const HTTP_METHOD_CONFIG: Record<HttpMethod, {
  label: string;
  color: string;
  bgColor: string;
  description: string;
}> = {
  GET: {
    label: 'GET',
    color: '#10B981',
    bgColor: 'rgba(16, 185, 129, 0.1)',
    description: 'Retrieve a resource',
  },
  POST: {
    label: 'POST',
    color: '#F59E0B',
    bgColor: 'rgba(245, 158, 11, 0.1)',
    description: 'Create a new resource',
  },
  PUT: {
    label: 'PUT',
    color: '#3B82F6',
    bgColor: 'rgba(59, 130, 246, 0.1)',
    description: 'Replace a resource',
  },
  PATCH: {
    label: 'PATCH',
    color: '#8B5CF6',
    bgColor: 'rgba(139, 92, 246, 0.1)',
    description: 'Partially update a resource',
  },
  DELETE: {
    label: 'DELETE',
    color: '#EF4444',
    bgColor: 'rgba(239, 68, 68, 0.1)',
    description: 'Delete a resource',
  },
  HEAD: {
    label: 'HEAD',
    color: '#6B7280',
    bgColor: 'rgba(107, 114, 128, 0.1)',
    description: 'Get response headers only',
  },
  OPTIONS: {
    label: 'OPTIONS',
    color: '#6B7280',
    bgColor: 'rgba(107, 114, 128, 0.1)',
    description: 'Get supported methods',
  },
};

// Status code categories
export const STATUS_CODE_CATEGORIES = {
  '2xx': { label: 'Success', color: '#10B981' },
  '3xx': { label: 'Redirect', color: '#F59E0B' },
  '4xx': { label: 'Client Error', color: '#EF4444' },
  '5xx': { label: 'Server Error', color: '#DC2626' },
};

// Common status codes
export const COMMON_STATUS_CODES: Record<string, string> = {
  '200': 'OK',
  '201': 'Created',
  '204': 'No Content',
  '301': 'Moved Permanently',
  '302': 'Found',
  '304': 'Not Modified',
  '400': 'Bad Request',
  '401': 'Unauthorized',
  '403': 'Forbidden',
  '404': 'Not Found',
  '405': 'Method Not Allowed',
  '409': 'Conflict',
  '422': 'Unprocessable Entity',
  '429': 'Too Many Requests',
  '500': 'Internal Server Error',
  '502': 'Bad Gateway',
  '503': 'Service Unavailable',
};

// Schema type configuration
export const SCHEMA_TYPE_CONFIG: Record<SchemaType, {
  label: string;
  icon: string;
  formats?: StringFormat[];
}> = {
  string: {
    label: 'String',
    icon: 'T',
    formats: ['date', 'date-time', 'email', 'uri', 'uuid', 'hostname', 'ipv4', 'ipv6', 'password', 'byte', 'binary'],
  },
  number: {
    label: 'Number',
    icon: '#',
  },
  integer: {
    label: 'Integer',
    icon: '#',
  },
  boolean: {
    label: 'Boolean',
    icon: '?',
  },
  array: {
    label: 'Array',
    icon: '[]',
  },
  object: {
    label: 'Object',
    icon: '{}',
  },
};

// Security scheme type configuration
export const SECURITY_SCHEME_CONFIG: Record<SecuritySchemeType, {
  label: string;
  description: string;
}> = {
  apiKey: {
    label: 'API Key',
    description: 'API key passed in header, query, or cookie',
  },
  http: {
    label: 'HTTP Auth',
    description: 'HTTP authentication (Basic, Bearer, etc.)',
  },
  oauth2: {
    label: 'OAuth 2.0',
    description: 'OAuth 2.0 authorization flows',
  },
  openIdConnect: {
    label: 'OpenID Connect',
    description: 'OpenID Connect Discovery',
  },
};

// Default response templates
export const DEFAULT_RESPONSES: Record<string, { description: string; schema?: SchemaDefinition }> = {
  '200': {
    description: 'Successful response',
  },
  '201': {
    description: 'Resource created successfully',
  },
  '400': {
    description: 'Bad request - invalid parameters',
    schema: {
      type: 'object',
      properties: {
        error: { type: 'string', description: 'Error message' },
        details: { type: 'array', items: { type: 'string' }, description: 'Validation error details' },
      },
    },
  },
  '401': {
    description: 'Unauthorized - authentication required',
    schema: {
      type: 'object',
      properties: {
        error: { type: 'string', description: 'Error message' },
      },
    },
  },
  '403': {
    description: 'Forbidden - insufficient permissions',
    schema: {
      type: 'object',
      properties: {
        error: { type: 'string', description: 'Error message' },
      },
    },
  },
  '404': {
    description: 'Resource not found',
    schema: {
      type: 'object',
      properties: {
        error: { type: 'string', description: 'Error message' },
      },
    },
  },
  '500': {
    description: 'Internal server error',
    schema: {
      type: 'object',
      properties: {
        error: { type: 'string', description: 'Error message' },
        requestId: { type: 'string', description: 'Request ID for support' },
      },
    },
  },
};
